import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi } from '../api';
import { centsToDollarString, centsToDollarInput, dollarsToCents, fmtDate, TYPE_OPTIONS } from '../constants';
import { MoneyInput } from './MoneyInput';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const labelCls = 'text-xs text-gray-400 mb-1 block';
const sectionCls = 'text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider';
const money = (c) => (c == null ? '—' : centsToDollarString(c));

const INITIAL_STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
];

export const ConvertToProjectDialog = ({ open, onOpenChange, quote, onConverted }) => {
  const [step, setStep] = useState('form');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && quote) {
      setStep('form');
      setForm({
        name: quote.title || '',
        project_type: 'other',
        custom_project_type: '',
        status: 'planning',
        project_manager: '',
        start_date: '',
        target_completion_date: '',
        customer_budget: '',
        internal_cost_budget: quote.totals.internal_cost_complete ? centsToDollarInput(quote.totals.internal_expected_cost_cents) : '',
        description: quote.scope_summary || '',
        internal_notes: '',
      });
    }
  }, [open, quote]);

  if (!quote || !form) return null;
  const t = quote.totals;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toConfirm = () => {
    if (!form.name.trim()) return toast.error('Project name is required');
    if (form.start_date && form.target_completion_date && form.target_completion_date < form.start_date)
      return toast.error('Target completion date cannot be before start date');
    setStep('confirm');
  };

  const convert = async () => {
    setBusy(true);
    try {
      const r = await quotesApi.convertToProject(quote.id, {
        name: form.name,
        project_type: form.project_type,
        custom_project_type: form.project_type === 'other' ? form.custom_project_type || null : null,
        status: form.status,
        project_manager: form.project_manager || null,
        start_date: form.start_date || null,
        target_completion_date: form.target_completion_date || null,
        customer_budget_cents: dollarsToCents(form.customer_budget),
        internal_cost_budget_cents: dollarsToCents(form.internal_cost_budget),
        description: form.description || null,
        internal_notes: form.internal_notes || null,
      });
      if (r.data.already_converted) {
        toast.info('This quote was already converted. Opening the existing Project.');
      } else {
        toast.success('Project created from accepted Quote.');
      }
      onOpenChange(false);
      onConverted(r.data.project);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not convert quote');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="convert-dialog">
        <DialogHeader><DialogTitle>Convert Accepted Quote to Project</DialogTitle></DialogHeader>

        {/* Quote summary + financial snapshot (always visible) */}
        <div className="bg-[#0D0D0D] border border-gray-800 rounded p-3 text-sm space-y-1" data-testid="convert-quote-summary">
          <div className="flex justify-between"><span className="text-gray-500">Quote</span><span className="text-white">{quote.quote_number} · {quote.title}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="text-gray-300">{quote.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Accepted</span>
            <span className="text-gray-300">{fmtDate(quote.accepted_at?.slice(0, 10))} · {quote.accepted_via === 'customer_link' ? 'Approved Online' : 'Accepted Internally'}</span></div>
          <div className="border-t border-gray-800 my-2" />
          <div className="flex justify-between"><span className="text-gray-500">Accepted Quote Total{t.discount_percent != null && <span className="text-[#D4AF37]"> (incl. {t.discount_percent}% discount)</span>}</span><span className="text-[#D4AF37] font-semibold" data-testid="convert-quote-total">{money(t.quote_total_cents)}</span></div>
        </div>

        {step === 'form' ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className={sectionCls}>Project</div>
              <div><label className={labelCls}>Project Name *</label><Input value={form.name} onChange={set('name')} className={inputCls} data-testid="convert-name-input" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Project Type *</label>
                  <select value={form.project_type} onChange={set('project_type')} className={selectCls} data-testid="convert-type-select">
                    {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {form.project_type === 'other' ? (
                  <div><label className={labelCls}>Custom Type</label><Input value={form.custom_project_type} onChange={set('custom_project_type')} className={inputCls} data-testid="convert-custom-type-input" /></div>
                ) : <div />}
                <div>
                  <label className={labelCls}>Initial Status</label>
                  <select value={form.status} onChange={set('status')} className={selectCls} data-testid="convert-status-select">
                    {INITIAL_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div><label className={labelCls}>Project Manager</label><Input value={form.project_manager} onChange={set('project_manager')} className={inputCls} data-testid="convert-manager-input" /></div>
            </div>
            <div className="space-y-3">
              <div className={sectionCls}>Dates</div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Start Date</label><Input type="date" value={form.start_date} onChange={set('start_date')} className={inputCls} data-testid="convert-start-date" /></div>
                <div><label className={labelCls}>Target Completion</label><Input type="date" value={form.target_completion_date} onChange={set('target_completion_date')} className={inputCls} data-testid="convert-target-date" /></div>
              </div>
            </div>
            <div className="space-y-3">
              <div className={sectionCls}>Financial Setup</div>
              <div>
                <label className={labelCls}>Contract Value (read-only)</label>
                <Input value={money(t.customer_subtotal_cents)} readOnly disabled className={`${inputCls} opacity-70`} data-testid="convert-contract-readonly" />
                <p className="text-[11px] text-gray-500 mt-1">This becomes the Project's Current Contract Value and Original Contract Value.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Project Internal Cost Budget ($)</label>
                  <MoneyInput value={form.internal_cost_budget} onChange={(v) => setForm((x) => ({ ...x, internal_cost_budget: v }))} placeholder="Leave blank if unset" className={inputCls} data-testid="convert-internal-budget-input" />
                  <p className="text-[11px] text-gray-500 mt-1">This is the current internal Project budget. The original Quote expected cost will still be preserved separately.</p>
                </div>
                <div>
                  <label className={labelCls}>Customer Budget ($)</label>
                  <MoneyInput value={form.customer_budget} onChange={(v) => setForm((x) => ({ ...x, customer_budget: v }))} placeholder="Optional" className={inputCls} data-testid="convert-customer-budget-input" />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className={sectionCls}>Description</div>
              <div><label className={labelCls}>Project Description</label><Textarea value={form.description} onChange={set('description')} rows={3} className={inputCls} data-testid="convert-description-input" /></div>
              <div><label className={labelCls}>Project Internal Notes</label><Textarea value={form.internal_notes} onChange={set('internal_notes')} rows={2} className={inputCls} data-testid="convert-notes-input" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300" data-testid="convert-cancel-btn">Cancel</Button>
              <Button onClick={toConfirm} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="convert-continue-btn">
                Continue <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="convert-confirm-step">
            <div className="bg-[#0D0D0D] border border-[#D4AF37]/40 rounded p-4 space-y-2 text-sm">
              <div className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider">Create Project</div>
              <div className="text-white font-semibold text-base" data-testid="convert-confirm-name">{form.name}</div>
              <div className="text-gray-400 text-xs">From Quote {quote.quote_number}</div>
              <div className="border-t border-gray-800 my-2" />
              <div className="flex justify-between"><span className="text-gray-500">Current Contract Value</span><span className="text-[#D4AF37] font-semibold">{money(t.customer_subtotal_cents)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Project Internal Cost Budget</span>
                <span className="text-gray-200">{form.internal_cost_budget === '' ? 'Not set' : centsToDollarString(dollarsToCents(form.internal_cost_budget))}</span></div>
            </div>
            <p className="text-xs text-gray-500">The Accepted Quote will remain preserved and linked to the Project.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep('form')} disabled={busy} className="border-gray-700 text-gray-300" data-testid="convert-back-btn">Back</Button>
              <Button onClick={convert} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="convert-confirm-btn">
                {busy && <Loader2 size={14} className="animate-spin" />} Create Project
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
